import { useCallback, useState } from 'react';
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { HTTPError, TimeoutError } from 'ky';
import { api } from 'lib/request/api';
import type { ScanDepth } from 'pages/admin/utils';
import type {
  FailureScan,
  FailureSetName,
  QueueScan,
  TimedQueuesSummary,
} from './types';

/** One click of a scan button. `run` makes each click its own request. */
export interface ScanRequest<T extends string> {
  target: T;
  scanDepth: ScanDepth;
  run: number;
}

const timedQueueKeys = ['admin', 'sidekiq-timed-queues'] as const;

/**
 * Reads the polled summary: depth, latency and paused flag per timed queue,
 * the retry and dead set sizes, and the running work set. Every read is O(1)
 * per queue or bounded by the worker thread count, so it is safe to poll.
 *
 * @param {number} pollingInterval - Milliseconds between refreshes; 0 turns polling off.
 * @return The summary query.
 */
export const useTimedQueuesSummary = (pollingInterval: number) =>
  useQuery(
    queryOptions({
      queryKey: [...timedQueueKeys, 'summary'] as const,
      queryFn: ({ signal }) =>
        api
          .get('admin/sidekiq_timed_queues', { signal })
          .json<TimedQueuesSummary>(),
      refetchInterval: pollingInterval > 0 ? pollingInterval : false,
    })
  );

/**
 * Turns a failed scan into a message worth showing: a timeout says to scan
 * less, and a server error shows the server's own message (the
 * ApplicationError `{ errors: [{ message }] }` envelope).
 *
 * @param {Promise<T>} scan - The scan request.
 * @return {Promise<T>} The scan result; rejects with a readable Error.
 */
export const readableScan = async <T>(scan: Promise<T>): Promise<T> => {
  try {
    return await scan;
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new Error(
        'The scan did not answer in time. Choose a smaller scan depth.',
        { cause: error }
      );
    }
    if (error instanceof HTTPError) {
      const body = (await error.response.json().catch(() => null)) as {
        errors?: { message?: string }[];
      } | null;
      throw new Error(body?.errors?.[0]?.message ?? error.message, {
        cause: error,
      });
    }
    throw error;
  }
};

/**
 * An on-demand scan of `path/:target`: runs only once a scan has been asked
 * for (skipToken until then), and never refetches on its own, since a deep
 * scan of a large queue takes seconds. The target is always URL-encoded.
 *
 * @param {string} path - The endpoint, without a leading slash; it also keys the query.
 * @param {ScanRequest | null} request - The last scan asked for, or null before the first.
 * @return The query options.
 */
const scanOptions = <R>(path: string, request: ScanRequest<string> | null) =>
  queryOptions({
    queryKey: [...timedQueueKeys, path, request] as const,
    queryFn: request
      ? ({ signal }) =>
          readableScan(
            api
              .get(`${path}/${encodeURIComponent(request.target)}`, {
                signal,
                searchParams: { sample_limit: request.scanDepth },
              })
              .json<R>()
          )
      : skipToken,
    staleTime: Infinity,
  });

/**
 * Reads the job-class scan of one queue.
 *
 * @param {ScanRequest | null} request - The last scan asked for, or null before the first.
 * @return The scan query.
 */
export const useQueueScan = (request: ScanRequest<string> | null) =>
  useQuery(scanOptions<QueueScan>('admin/sidekiq_timed_queues', request));

/**
 * Reads the retry or dead set breakdown.
 *
 * @param {ScanRequest | null} request - The last scan asked for, or null before the first.
 * @return The scan query.
 */
export const useFailureScan = (request: ScanRequest<FailureSetName> | null) =>
  useQuery(scanOptions<FailureScan>('admin/sidekiq_failure_sets', request));

/**
 * The scan a section last asked for, and the trigger for the next one. Each
 * call to `startScan` is its own request, even for the same target and depth.
 *
 * @return The last request (null before the first) and the trigger.
 */
export const useScanRequest = <T extends string>() => {
  const [request, setRequest] = useState<ScanRequest<T> | null>(null);
  const startScan = useCallback((target: T, scanDepth: ScanDepth) => {
    setRequest((previous) => ({
      target,
      scanDepth,
      run: (previous?.run ?? 0) + 1,
    }));
  }, []);

  return { request, startScan };
};
